import { getIntercomLanguageOverride } from "@kaleo/account-localization/intercom";
import { fetchAppRequest } from "./app-request";
import { APP_ORIGIN, IS_PRODUCTION_DOCS } from "./localization";

const INTERCOM_APP_ID = "zgj20vlu";
const INTERCOM_API_BASE = "https://api-iam.intercom.io";
const TOKEN_REFRESH_LEEWAY_MS = 60_000;
const FOCUS_REVALIDATE_INTERVAL_MS = 5_000;
const CONTEXT_UPDATE_DELAY_MS = 500;
const REFRESH_RETRY_DELAYS_MS = [5_000, 15_000, 30_000] as const;
const INTERCOM_HORIZONTAL_PADDING_PX = 20;
const INTERCOM_VERTICAL_PADDING_PX = 20;
const DOCS_MAX_LAYOUT_WIDTH_PX = 1536;

export const INTERCOM_OPEN_EVENT = "kaleo:intercom:open";

export type IntercomThemeMode = "dark" | "light";

export type IntercomContext = {
  languageOverride: string;
  launcherHorizontalPadding: number;
  pagePath: string;
  themeMode: IntercomThemeMode;
};

export function isTrustedDocsHostname(hostname: string): boolean {
  return hostname === "docs.withkaleo.com";
}

type AnonymousIdentity = {
  kind: "anonymous";
};

type IdentifiedIdentity = {
  kind: "identified";
  exp: number;
  iat: number;
  jwt: string;
  userId: string;
};

export type IntercomIdentity = AnonymousIdentity | IdentifiedIdentity;
export type BootIdentity = "idle" | "anonymous" | `identified:${string}`;
export type IntercomSynchronizationAction =
  | "initialize"
  | "replace"
  | "reload"
  | "update"
  | "none";

type IntercomSdk = typeof import("@intercom/messenger-js-sdk");
type IntercomSettings = Parameters<IntercomSdk["boot"]>[0];

let activeIdentity: BootIdentity = "idle";
let currentContext: IntercomContext | null = null;
let identityCheckedAt = 0;
let identifiedRefreshAt = 0;
let refreshTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
let refreshRetryIndex = 0;
let refreshRetryNotBefore = 0;
let contextUpdateTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
let appliedContextKey = "";
let appliedLanguageOverride = "";
let synchronizePromise: Promise<boolean> | null = null;

function getIdentityKey(
  identity: IntercomIdentity,
): Exclude<BootIdentity, "idle"> {
  return identity.kind === "anonymous"
    ? "anonymous"
    : `identified:${identity.userId}`;
}

export function getIntercomIdentityTransition(
  current: BootIdentity,
  next: Exclude<BootIdentity, "idle">,
): "initialize" | "replace" | "update" {
  if (current === "idle") return "initialize";
  return current === next ? "update" : "replace";
}

export function getIntercomSynchronizationAction(
  {
    activeJwtNearRefresh,
    contextChanged,
    current,
    languageChanged,
    next,
  }: {
    activeJwtNearRefresh: boolean;
    contextChanged: boolean;
    current: BootIdentity;
    languageChanged: boolean;
    next: Exclude<BootIdentity, "idle">;
  },
): IntercomSynchronizationAction {
  const transition = getIntercomIdentityTransition(current, next);
  if (transition !== "update") return transition;

  // Intercom persists language_override on update, but the loaded Messenger
  // does not render that language until its next session.
  if (languageChanged) return "reload";

  return contextChanged ||
    (next.startsWith("identified:") && activeJwtNearRefresh)
    ? "update"
    : "none";
}

export function getIntercomRefreshRetryDelay(
  retryIndex: number,
): number | null {
  return REFRESH_RETRY_DELAYS_MS[retryIndex] ?? null;
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  return globalThis.atob(base64 + padding);
}

export function readIntercomJwtIdentity(jwt: string): {
  exp: number;
  iat: number;
  userId: string;
} {
  const payloadSegment = jwt.split(".")[1];
  if (!payloadSegment) throw new Error("intercom_token_response_invalid");

  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64Url(payloadSegment));
  } catch {
    throw new Error("intercom_token_response_invalid");
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("intercom_token_response_invalid");
  }

  const { exp, iat, user_id: userId } = payload as {
    exp?: unknown;
    iat?: unknown;
    user_id?: unknown;
  };
  if (
    typeof exp !== "number" ||
    !Number.isFinite(exp) ||
    typeof iat !== "number" ||
    !Number.isFinite(iat) ||
    !Number.isFinite(exp - iat) ||
    exp <= iat ||
    typeof userId !== "string" ||
    !userId
  ) {
    throw new Error("intercom_token_response_invalid");
  }

  return { exp, iat, userId };
}

export async function fetchIntercomIdentity(
  request: typeof fetchAppRequest = fetchAppRequest,
): Promise<IntercomIdentity> {
  const response = await request(
    `${APP_ORIGIN}/api/user/intercom-token`,
    {
      cache: "no-store",
      credentials: "include",
    },
  );

  if (response.status === 401) return { kind: "anonymous" };
  if (!response.ok) {
    throw new Error(`intercom_token_request_failed:${response.status}`);
  }

  const body = (await response.json().catch(() => null)) as {
    intercomUserJwt?: unknown;
  } | null;
  if (
    typeof body?.intercomUserJwt !== "string" ||
    !body.intercomUserJwt
  ) {
    throw new Error("intercom_token_response_invalid");
  }

  const { exp, iat, userId } = readIntercomJwtIdentity(
    body.intercomUserJwt,
  );
  return {
    kind: "identified",
    exp,
    iat,
    jwt: body.intercomUserJwt,
    userId,
  };
}

export function getIntercomCookieDomains(
  hostname: string,
): Array<string | null> {
  const normalizedHostname = hostname.toLowerCase();
  const domains = new Set<string | null>([null]);
  if (normalizedHostname) domains.add(normalizedHostname);
  if (
    normalizedHostname === "withkaleo.com" ||
    normalizedHostname.endsWith(".withkaleo.com")
  ) {
    domains.add("withkaleo.com");
  }
  return [...domains];
}

function expireIntercomCookies(): void {
  if (
    typeof globalThis.document === "undefined" ||
    typeof globalThis.window === "undefined"
  ) {
    return;
  }

  const names = globalThis.document.cookie
    .split(";")
    .map((entry) => entry.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name?.startsWith("intercom-")));

  for (const name of new Set(names)) {
    for (const domain of getIntercomCookieDomains(
      globalThis.window.location.hostname,
    )) {
      globalThis.document.cookie = [
        `${name}=`,
        "Expires=Thu, 01 Jan 1970 00:00:00 GMT",
        "Max-Age=0",
        "Path=/",
        ...(domain ? [`Domain=${domain}`] : []),
      ].join("; ");
    }
  }
}

export function getIntercomLauncherHorizontalPadding(
  viewportWidth: number,
  sidebarCollapsed = false,
): number {
  if (
    sidebarCollapsed ||
    !Number.isFinite(viewportWidth) ||
    viewportWidth <= DOCS_MAX_LAYOUT_WIDTH_PX
  ) {
    return INTERCOM_HORIZONTAL_PADDING_PX;
  }

  return (
    INTERCOM_HORIZONTAL_PADDING_PX +
    Math.round((viewportWidth - DOCS_MAX_LAYOUT_WIDTH_PX) / 2)
  );
}

export function createIntercomSettings(
  identity: IntercomIdentity,
  context: IntercomContext,
): IntercomSettings {
  return {
    alignment: "left",
    api_base: INTERCOM_API_BASE,
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: false,
    horizontal_padding: context.launcherHorizontalPadding,
    language_override: context.languageOverride,
    theme_mode: context.themeMode,
    vertical_padding: INTERCOM_VERTICAL_PADDING_PX,
    ...(identity.kind === "identified"
      ? { intercom_user_jwt: identity.jwt }
      : {}),
  };
}

function getContextKey(context: IntercomContext): string {
  return [
    context.languageOverride,
    context.themeMode,
    context.pagePath,
    context.launcherHorizontalPadding,
  ].join(":");
}

function isActiveJwtNearRefresh(now = Date.now()): boolean {
  return (
    activeIdentity.startsWith("identified:") &&
    identifiedRefreshAt > 0 &&
    now >= identifiedRefreshAt
  );
}

export function getIntercomJwtRefreshDeadline(
  iat: number,
  exp: number,
  receivedAt: number,
): number {
  const lifetimeMs = (exp - iat) * 1000;
  const leeway = Math.min(TOKEN_REFRESH_LEEWAY_MS, lifetimeMs / 2);
  return receivedAt + Math.max(1_000, lifetimeMs - leeway);
}

function clearRefreshTimer(): void {
  if (refreshTimer !== null) {
    globalThis.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function setRefreshTimer(delay: number, retry = false): void {
  refreshTimer = globalThis.setTimeout(() => {
    refreshTimer = null;
    if (retry) refreshRetryNotBefore = 0;
    if (currentContext) void synchronizeIntercom(currentContext);
  }, delay);
}

function scheduleRefresh(identity: IntercomIdentity): void {
  clearRefreshTimer();
  refreshRetryIndex = 0;
  refreshRetryNotBefore = 0;
  if (identity.kind !== "identified") {
    identifiedRefreshAt = 0;
    return;
  }

  const now = Date.now();
  identifiedRefreshAt = getIntercomJwtRefreshDeadline(
    identity.iat,
    identity.exp,
    now,
  );
  setRefreshTimer(identifiedRefreshAt - now);
}

function scheduleRefreshRetry(): void {
  clearRefreshTimer();
  const delay = getIntercomRefreshRetryDelay(refreshRetryIndex);
  if (delay === null) {
    refreshRetryNotBefore = Number.POSITIVE_INFINITY;
    return;
  }

  refreshRetryIndex += 1;
  refreshRetryNotBefore = Date.now() + delay;
  setRefreshTimer(delay, true);
}

export function shouldPassivelyRevalidateIntercom(
  identity: BootIdentity,
  checkedAt: number,
  retryNotBefore: number,
  now: number,
): boolean {
  return (
    identity !== "idle" &&
    now >= retryNotBefore &&
    now - checkedAt >= FOCUS_REVALIDATE_INTERVAL_MS
  );
}

async function applyIdentity(
  sdk: IntercomSdk,
  identity: IntercomIdentity,
  context: IntercomContext,
  action: Exclude<IntercomSynchronizationAction, "none">,
): Promise<void> {
  const nextIdentity = getIdentityKey(identity);
  const settings = createIntercomSettings(identity, context);
  const hasExistingInstance =
    typeof globalThis.window !== "undefined" &&
    typeof globalThis.window.Intercom === "function";

  applyIntercomSdkTransition(
    sdk,
    settings,
    action,
    hasExistingInstance,
    expireIntercomCookies,
  );

  activeIdentity = nextIdentity;
  appliedContextKey = getContextKey(context);
  appliedLanguageOverride = context.languageOverride;
  identityCheckedAt = Date.now();
  scheduleRefresh(identity);
}

export function applyIntercomSdkTransition(
  sdk: Pick<IntercomSdk, "boot" | "Intercom" | "shutdown" | "update">,
  settings: IntercomSettings,
  action: Exclude<IntercomSynchronizationAction, "none">,
  hasExistingInstance: boolean,
  cleanupCookies: () => void,
): void {
  if (action === "initialize") {
    if (hasExistingInstance) sdk.shutdown();
    // A fresh document cannot prove which prior user an Intercom cookie
    // represents. Clear visible state before the first verified boot so a
    // shared browser cannot resume or merge another user's conversation.
    cleanupCookies();
    if (hasExistingInstance) sdk.boot(settings);
    // Vite wraps this CommonJS package's default export as a namespace object.
    // The named initializer remains a function in both source and production.
    else sdk.Intercom(settings);
  } else if (action === "update") {
    sdk.update(settings);
  } else {
    sdk.shutdown();
    if (action === "replace") cleanupCookies();
    sdk.boot(settings);
  }
}

async function synchronizeIntercom(
  context: IntercomContext,
): Promise<boolean> {
  currentContext = context;
  if (
    !IS_PRODUCTION_DOCS ||
    typeof globalThis.window === "undefined" ||
    !isTrustedDocsHostname(globalThis.window.location.hostname)
  ) {
    return false;
  }
  if (synchronizePromise) return synchronizePromise;

  synchronizePromise = (async () => {
    try {
      const identity = await fetchIntercomIdentity();
      const nextContext = currentContext ?? context;
      const action = getIntercomSynchronizationAction({
        activeJwtNearRefresh: isActiveJwtNearRefresh(),
        contextChanged: appliedContextKey !== getContextKey(nextContext),
        current: activeIdentity,
        languageChanged:
          appliedLanguageOverride !== nextContext.languageOverride,
        next: getIdentityKey(identity),
      });

      identityCheckedAt = Date.now();
      // The fetched JWT is not active until it is sent to Intercom. A no-op
      // must therefore keep the existing token's refresh timer unchanged.
      if (action === "none") return true;

      const sdk = await import("@intercom/messenger-js-sdk");
      await applyIdentity(sdk, identity, nextContext, action);
      return true;
    } catch {
      if (activeIdentity === "idle" || isActiveJwtNearRefresh()) {
        scheduleRefreshRetry();
      }

      // Keep an already verified session alive, but tell this caller that
      // revalidation failed. In particular, an explicit open must never show a
      // possibly stale user's conversation after a failed identity check.
      return false;
    } finally {
      synchronizePromise = null;
    }
  })();

  return synchronizePromise;
}

export async function openIntercomMessenger(
  context: IntercomContext,
  dependencies?: {
    show: () => void | Promise<void>;
    synchronize: (context: IntercomContext) => Promise<boolean>;
  },
): Promise<boolean> {
  try {
    const ready = await (
      dependencies?.synchronize ?? synchronizeIntercom
    )(context);
    if (!ready) return false;

    if (dependencies) {
      await dependencies.show();
    } else {
      const { show } = await import("@intercom/messenger-js-sdk");
      show();
    }
    return true;
  } catch {
    return false;
  }
}

export async function updateIntercomContext(
  context: IntercomContext,
): Promise<void> {
  currentContext = context;
  if (activeIdentity === "idle") {
    if (Date.now() < refreshRetryNotBefore) return;
    await synchronizeIntercom(context);
    return;
  }
  if (appliedLanguageOverride !== context.languageOverride) {
    if (contextUpdateTimer !== null) {
      globalThis.clearTimeout(contextUpdateTimer);
      contextUpdateTimer = null;
    }
    await synchronizeIntercom(context);
    return;
  }
  if (appliedContextKey === getContextKey(context)) {
    return;
  }

  if (contextUpdateTimer !== null) {
    globalThis.clearTimeout(contextUpdateTimer);
  }
  contextUpdateTimer = globalThis.setTimeout(() => {
    contextUpdateTimer = null;
    if (
      activeIdentity === "idle" ||
      !currentContext ||
      appliedContextKey === getContextKey(currentContext)
    ) {
      return;
    }

    const nextContext = currentContext;
    void import("@intercom/messenger-js-sdk").then(({ update }) => {
      if (activeIdentity === "idle") return;
      update({
        alignment: "left",
        horizontal_padding: nextContext.launcherHorizontalPadding,
        language_override: nextContext.languageOverride,
        theme_mode: nextContext.themeMode,
        vertical_padding: INTERCOM_VERTICAL_PADDING_PX,
      });
      appliedContextKey = getContextKey(nextContext);
    });
  }, CONTEXT_UPDATE_DELAY_MS);
}

export async function revalidateIntercomIfDue(
  context: IntercomContext,
): Promise<void> {
  // Focus, pageshow, and visibilitychange often fire together. Revalidate on
  // every actual return while coalescing that burst, so a dashboard sign-out
  // or account switch cannot leave a stale Messenger identity in the docs tab.
  // A scheduled refresh failure owns its retry window so these passive events
  // cannot accidentally bypass the bounded backoff.
  if (
    shouldPassivelyRevalidateIntercom(
      activeIdentity,
      identityCheckedAt,
      refreshRetryNotBefore,
      Date.now(),
    )
  ) {
    await synchronizeIntercom(context);
  }
}

export function requestIntercomMessenger(): void {
  if (typeof globalThis.window === "undefined") return;
  globalThis.window.dispatchEvent(new Event(INTERCOM_OPEN_EVENT));
}

export { getIntercomLanguageOverride };
